import { observer } from 'mobx-react-lite'
import { Button, CollapseWithIcon, SpinnerLoader, Tabs, Tooltip, Typography } from 'shared/ui'
import { LINK_AIRCALL_INTEGRATING_GUIDE } from 'shared/constants/links'
import sharedStyles from 'pages/settings/pages/integrations/pages/integrationSettings/ui/styles.module.scss'
import { type IntegrationCollapseSettingsStore } from 'pages/settings/pages/integrations/pages/integrationSettings/store/IntegrationCollapseSettingsStore'

import { type AircallNumbersStore } from 'pages/settings/pages/integrations/pages/integrationSettings/IntegrationAircallSettings/store/AircallNumbersStore'
import { AssignedNumbers } from 'pages/settings/pages/integrations/pages/integrationSettings/IntegrationAircallSettings/ui/AssignedNumbers/AssignedNumbers'
import { UnassignedNumbers } from 'pages/settings/pages/integrations/pages/integrationSettings/IntegrationAircallSettings/ui/UnassignedNumbers/UnassignedNumbers'
import { useIntegrationDetailsContext } from 'pages/settings/pages/integrations/context/integrationDetailsContext'
import styles from './styles.module.scss'

type ICollapseAircallNumbersProps = {
  collapseStore: IntegrationCollapseSettingsStore
  aircallNumbersStore: AircallNumbersStore
}

export const CollapseAircallNumbers = observer(
  ({ collapseStore, aircallNumbersStore }: ICollapseAircallNumbersProps) => {
    const { numbersListStore, activeTabKey, setActiveTabKey } = aircallNumbersStore
    const { isAircallNumbersImporting } = useIntegrationDetailsContext()

    const assignedNumbersLength = numbersListStore.assignedNumbersLength
    const unassignedNumbersLength = numbersListStore.unassignedNumbersLength

    const getTableData = () => {
      if (isAircallNumbersImporting) {
        return (
          <div className={styles.spinnerLoaderWrapper}>
            <SpinnerLoader
              ariaLabel={'CollapseAircallNumbers'}
              wrapperClassName={styles.spinnerLoader}
            />
            <Typography
              ariaLabel='initializing'
              variant='body-md-medium'
              color='var(--content-primary-tertiary)'
            >
              Import is in progress
            </Typography>
          </div>
        )
      }

      return activeTabKey === 'assigned' ? (
        <AssignedNumbers store={aircallNumbersStore} />
      ) : (
        <UnassignedNumbers
          store={aircallNumbersStore}
          aircallLink={aircallNumbersStore.aircallLink}
        />
      )
    }

    return (
      <CollapseWithIcon
        opened={collapseStore.opened('aircallNumbers')}
        onClick={collapseStore.onToggle('aircallNumbers')}
        icon={'userLine'}
        title={
          <Typography ariaLabel='aircallNumbersTitle' variant='body-md-medium'>
            Aircall numbers
            <Typography
              ariaLabel='assignedNumbersLength'
              variant='body-md-medium'
              color='var(--content-primary-tertiary)'
              className={styles.assignedNumbersLength}
            >
              ({assignedNumbersLength} {' / '} {unassignedNumbersLength + assignedNumbersLength})
            </Typography>
          </Typography>
        }
        label='Use your existing Aircall phone number with Salesmsg texting'
        headerClassName={sharedStyles.collapseWithIcon}
      >
        <div className={styles.tabsWrapper}>
          <Tabs
            variant='simpleWithoutBorder'
            tabs={[
              { key: 'assigned', name: 'Assigned', count: assignedNumbersLength },
              { key: 'unassigned', name: 'Unassigned', count: unassignedNumbersLength },
            ]}
            countVariant='white'
            activeTabKey={activeTabKey}
            handleSelectTab={(tab) => {
              setActiveTabKey(tab.key)
              numbersListStore.clearRejectAssignedNumbers()
            }}
            tabsClassName={styles.tabs}
          />
          <Tooltip
            label={
              <Typography
                ariaLabel='readMore'
                variant='body-sm-regular'
                className={styles.readMore}
              >
                Read more
                <a target={'_blank'} href={LINK_AIRCALL_INTEGRATING_GUIDE} rel='noreferrer'>
                  here
                </a>
              </Typography>
            }
            placement='top'
          >
            <Button
              className={styles.addNumberButton}
              icon='add'
              text={'Add Aircall number'}
              contained={'secondary'}
              tag={'a'}
              target={'_blank'}
              href={aircallNumbersStore.aircallLink}
            />
          </Tooltip>
        </div>
        {getTableData()}
      </CollapseWithIcon>
    )
  }
)
